import type {EnhancedStore} from '@reduxjs/toolkit'
import {configureStore} from '@reduxjs/toolkit'
import {authApi} from '@apis/auth'
import {baseApi} from '@apis/base'
import {edgeApi} from '@apis/edgeAPI'
import {anomalyDetectionAPI} from '@app/apis/anomalyDetection'
import {aiCoreAPI} from '@apis/aiCoreAPI'
import {infrastructureTenantsApi} from '@apis/infrastructureTenants'
import edgeApiConnectionFormSlice from '@containers/Dashboards/EdgeApi/redux/slices/edgeApiConnectionFormSlice.ts'
import edgeApiNavigationFormSlice from '@containers/Dashboards/EdgeApi/redux/slices/edgeApiNavigationFormSlice.ts'
import anomalies from '@redux/slices/anomalies'
import authReducer from '@redux/slices/authSlice'
import batches from '@redux/slices/batches'
import batchFocusMode from '@redux/slices/batchFocusMode'
import batchRecomputeOffer from '@redux/slices/batchRecomputeOffer'
import filterByClickDigitalTwin from '@redux/slices/filterByClickDigitalTwin'
import globalMode from '@redux/slices/globalMode'
import programs from '@redux/slices/programs'
import signals from '@redux/slices/signals'
import signalsScreenModes from '@redux/slices/signalsScreenModes'
import sliceTooltip from '@redux/slices/sliceTooltip'
import snackbar from '@redux/slices/snackbar'
import timemode from '@redux/slices/timemode'
import timeseries from '@redux/slices/timeseries'
import tsampling from '@redux/slices/tsampling'
import user from '@redux/slices/user'
import customDashboard from '@redux/slices/customDashboard'

export const setupStore = (preloadedState: Record<string, unknown>): EnhancedStore =>
  configureStore({
    reducer: {
      batches,
      programs,
      timeseries,
      tSampling: tsampling,
      timemode,
      signals,
      snackbar,
      anomalies,
      signalsScreenModes,
      user,
      sliceTooltip,
      filterByClickDigitalTwin,
      globalMode,
      edgeApiConnectionForm: edgeApiConnectionFormSlice,
      edgeApiNavigationForm: edgeApiNavigationFormSlice,
      batchFocusMode,
      batchRecomputeOffer,
      customDashboard,
      [baseApi.reducerPath]: baseApi.reducer,
      [authApi.reducerPath]: authApi.reducer,
      [edgeApi.reducerPath]: edgeApi.reducer,
      [anomalyDetectionAPI.reducerPath]: anomalyDetectionAPI.reducer,
      [aiCoreAPI.reducerPath]: aiCoreAPI.reducer,
      [infrastructureTenantsApi.reducerPath]: infrastructureTenantsApi.reducer,

      auth: authReducer,
    },
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: false,
      }).concat(
        baseApi.middleware,
        authApi.middleware,
        edgeApi.middleware,
        anomalyDetectionAPI.middleware,
        aiCoreAPI.middleware,
        infrastructureTenantsApi.middleware,
      ),
    // RTK Query marks its internal actions "autoBatched"; the default autoBatch
    // strategy defers subscriber notification to requestAnimationFrame, which
    // NEVER fires in a hidden/background tab — a fetch resolving there updates
    // the store but no component re-renders, leaving hooks visibly stuck on
    // isLoading/isFetching until some unrelated event flushes React (lists
    // "stop loading" mid-infinite-scroll). A timer flushes hidden tabs too.
    // RTK ≥2.12 fixes the raf strategy the same way (100ms setTimeout
    // fallback) — drop this override when we upgrade.
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers({autoBatch: {type: 'timer', timeout: 100}}),
  })

// Infer the `RootState` type from the store itself. The app's one store is
// created in App.tsx (and exposed to Cypress there).
export type RootState = ReturnType<ReturnType<typeof setupStore>['getState']>
